import { CompanyNameWithIcon } from '../CompanyNameWithIcon';
import { NarrativeTabs } from '../NarrativeTabs';

import { NarrativeForm } from '../../utils/schema';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange/useDebouncedOnChange';
import { memo } from 'react';
import { isEqual } from 'lodash';
import { PastAnnualRevenueEarnings } from '../../types';
import type { NarrativeSidebarCompany } from './narrativeSidebarFragment';
import { CalculationBreakDownOutput } from '../Calculators';

type Props = {
  authorDisplayName: string;
  company: NarrativeSidebarCompany;
  onChange: (values: Partial<NarrativeForm>) => void;
  onSetFairValue: (fairValue: number) => void;
  readOnly: boolean;
  values: NarrativeForm;
  onReset: () => void;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  narrativeId?: string;
  calculationBreakDownOutput: CalculationBreakDownOutput;
};

const NarrativeSidebarBase = ({
  authorDisplayName,
  company,
  onChange,
  onSetFairValue,
  readOnly,
  values,
  onReset,
  pastAnnualRevenueEarnings,
  narrativeId,
  calculationBreakDownOutput,
}: Props) => {
  const { immediateValues, handleChange } = useDebouncedOnChange({
    values,
    onChangePartial: onChange,
  });

  return (
    <div className="grid h-min grid-rows-[repeat(3,max-content)] bg-p-grey-100 lg:rounded-x2">
      <div className="grid justify-start p-x2 pb-x1">
        <CompanyNameWithIcon
          name={company.name}
          tickerSymbol={company.tickerSymbol}
          websiteUrl={company.info?.url}
          canonicalURL={company.canonicalURL}
        />
      </div>
      <NarrativeTabs
        company={company}
        onChange={handleChange}
        onSetFairValue={onSetFairValue}
        values={immediateValues}
        readOnly={readOnly}
        authorDisplayName={authorDisplayName}
        onReset={onReset}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        narrativeId={narrativeId}
        calculationBreakDownOutput={calculationBreakDownOutput}
      />
    </div>
  );
};

/**
 * Omitting content and title from values to prevent unnecessary re-renders (changes when users type inside the editor or header)
 */
const cleanProps = (props: Props) => {
  const { content, title, ...values } = props.values;
  return {
    ...props,
    values,
  };
};
export const NarrativeSidebar = memo(
  NarrativeSidebarBase,
  (prevProps, nextProps) => {
    return isEqual(cleanProps(prevProps), cleanProps(nextProps));
  }
);
